import {hasBillableMinutesSplitFeature} from './../../../admin-tab/time-management/time-registration-settings/TimeRegistrationSettingsSection';
import {Granularity} from '../../../admin-tab/time-management/constants/Granularity';

export type TimeRegistrationSettings = {
	granularity: Granularity;
	allowDoneTasks: boolean;
	allowProjects: boolean;
	allowInternalTime: boolean;
	allowBillableSplit: boolean;
	allowRoles: boolean;
	allowExceedingEstimate: boolean;
	allowOutsideTaskDates: boolean;
	allowOutsideProjectDates: boolean;
	requireTaskAssignment: boolean;
	requireNote: boolean;
};

const CACHE = new Map<string, TimeRegistrationSettings>();
const TimeRegistrationSettingsStorageKey = 'timeRegistrationSettings';

const DefaultSetup: TimeRegistrationSettings = {
	granularity: Granularity.Minutes,
	allowDoneTasks: true,
	allowProjects: true,
	allowInternalTime: true,
	allowExceedingEstimate: true,
	allowOutsideTaskDates: true,
	allowOutsideProjectDates: true,
	allowBillableSplit: false,
	allowRoles: false,
	requireTaskAssignment: false,
	requireNote: false,
};
export const TimeRegistrationSettingsDefaultSetup = DefaultSetup;

export const setTimeRegistrationSettings = (timeRegistrationSettings: TimeRegistrationSettings): void => {
	if (timeRegistrationSettings) {
		CACHE.clear();

		sessionStorage.setItem(TimeRegistrationSettingsStorageKey, JSON.stringify(timeRegistrationSettings));

		// populate cache
		CACHE.set(TimeRegistrationSettingsStorageKey, timeRegistrationSettings);
	}
};

const setTimeRegistrationSettingsCache = () => {
	if (CACHE.size === 0) {
		const sessionTimeRegistrationSettings = sessionStorage.getItem(TimeRegistrationSettingsStorageKey);
		const timeRegistrationSettings = sessionTimeRegistrationSettings
			? JSON.parse(sessionTimeRegistrationSettings)
			: DefaultSetup;
		setTimeRegistrationSettings(timeRegistrationSettings);
	}
};

// Don't use this, as not all TimeRegistrationSettings props align with the functions below - use the 'useTimeRegistrationSettings' hook, or the dedicated getter where possible.
export const getTimeRegistrationSettings = (): TimeRegistrationSettings => {
	setTimeRegistrationSettingsCache();
	const cachedTimeRegistrationSettings = CACHE.get(TimeRegistrationSettingsStorageKey);
	if (cachedTimeRegistrationSettings) {
		return cachedTimeRegistrationSettings;
	}

	return DefaultSetup;
};

export function isTimeRegistrationNoteRequired(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.requireNote;
}

export function isTimeRegistrationTaskAssignmentRequired(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.requireTaskAssignment;
}

export function isDoneTaskTimeRegistrationAllowed(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.allowDoneTasks;
}

export function isProjectTimeRegistrationAllowed(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.allowProjects;
}

export function isInternalTimeRegistrationAllowed(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.allowInternalTime;
}

export function isBillableSplitAllowed(): boolean {
	if (!hasBillableMinutesSplitFeature()) return DefaultSetup.allowBillableSplit;

	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.allowBillableSplit;
}

export function isRolesAllowed(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.allowRoles;
}

export function isExceedingEstimateAllowed(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.allowExceedingEstimate;
}

export function isOutsideTaskDatesAllowed(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.allowOutsideTaskDates;
}

export function isOutsideProjectDatesAllowed(): boolean {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.allowOutsideProjectDates;
}

export const getGranularity = (): Granularity => {
	const timeRegistrationSettings = getTimeRegistrationSettings();
	return timeRegistrationSettings.granularity;
};
